// ExploreDeck — the 3D card-galaxy experience. Opened as a full-screen
// modal from BuildDeck (auto) or DeckDetail (manual via "Explore deck"
// button).
//
// Two-tier layout:
//   1. FOREGROUND: 7 featured cards in a close arc facing the camera.
//      "Shuffle" cycles which 7 get promoted, role-balanced so the set
//      always shows the deck's shape.
//   2. BACKGROUND: the remaining mainboard cards orbit in 5 type-sorted
//      clusters (land / creature / enchantment / instant / sorcery /
//      other). Smaller, dimmer, further back — but still clickable for
//      the detail modal.
//
// Sideboard cards are excluded to keep the galaxy focused on the deck
// proper. Auto-opens after a fresh BuildDeck run unless the user has
// dismissed via "View classic display" (which sets a localStorage
// preference consumed by DeckDetail).
//
// Inspired by the 21st.dev "Stellar Card Gallery" pattern (starfield,
// concentric wireframe spheres, click-to-inspect modal) adapted for
// real Scryfall card imagery and our deck schema.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Plane, Html, Sphere } from "@react-three/drei";
import * as THREE from "three";
import { Shuffle, X, ZoomIn, ZoomOut, Rotate3d } from "lucide-react";
import type { Deck, DeckEntry } from "../lib/api";
import {
  getScryfallCollection,
  imageFor,
  backImageFor,
  type ScryfallCard,
} from "../lib/scryfall";
import { setAutoOpen } from "../lib/exploreDeckPref";
import { cn } from "../lib/cn";
import CardImage from "./CardImage";
import ManaCost from "./ManaCost";

interface Props {
  open: boolean;
  deck: Deck;
  /** Rendered above the galaxy. Usually the deck name/theme. */
  title?: string;
  /** Called when the user dismisses the experience. */
  onClose: () => void;
  /** If true, shown right after a fresh BuildDeck run — reveals the
   *  "Explore your deck" welcome copy. */
  welcome?: boolean;
}

interface DeckCard {
  name: string;
  /** Deck-level quantity of this card (e.g. 4 in a playset). */
  count: number;
  /** When a card appears as multiple physical copies, each copy gets its
   *  own DeckCard instance with a unique copyId so React keys and 3D
   *  positions stay stable. copyIndex is 1-based for UI labels. */
  copyId: string;
  copyIndex: number;
  card?: ScryfallCard;
}

interface Slot {
  x: number;
  y: number;
  z: number;
}

const FEATURED_COUNT = 7;

export default function ExploreDeck({
  open,
  deck,
  title,
  onClose,
  welcome = false,
}: Props) {
  const [allCards, setAllCards] = useState<DeckCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<DeckCard | null>(null);
  const [rotation, setRotation] = useState(0);

  // Mainboard flattened to one entry PER PHYSICAL COPY so a 4-of shows
  // as 4 tiles in the galaxy — matches the "first draw of the game"
  // framing where every card in the 60 matters.
  const entries = useMemo((): DeckCard[] => {
    const expanded: DeckCard[] = [];
    for (const e of deck.mainboard as DeckEntry[]) {
      for (let i = 1; i <= e.count; i++) {
        expanded.push({
          name: e.name,
          count: e.count,
          copyId: `${e.name}#${i}`,
          copyIndex: i,
        });
      }
    }
    return expanded;
  }, [deck]);

  useEffect(() => {
    if (!open || entries.length === 0) return;
    if (allCards.length > 0) return;
    setLoading(true);
    setLoadError(null);
    // De-dup names for the Scryfall lookup — the API only needs one
    // call per unique card — then fan the result back out across all
    // physical copies.
    const uniqueNames = Array.from(new Set(entries.map((e) => e.name)));
    getScryfallCollection(uniqueNames)
      .then((map) => {
        const hydrated = entries.map((e) => ({
          ...e,
          card: map.get(e.name.toLowerCase().trim().split("//")[0]!.trim()),
        }));
        setAllCards(hydrated);
        if (import.meta.env?.DEV) {
          const unresolved = hydrated
            .filter((c) => !c.card)
            .map((c) => c.name);
          if (unresolved.length > 0) {
            console.warn(
              "[ExploreDeck] unresolved card names:",
              Array.from(new Set(unresolved)),
            );
          }
        }
      })
      .catch((err) => {
        setLoadError(err?.message ?? "Failed to load card imagery");
      })
      .finally(() => setLoading(false));
  }, [open, entries, allCards.length]);

  // Keyboard: Esc closes detail modal first, then galaxy. Space shuffles.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (selectedCard) setSelectedCard(null);
        else onClose();
      } else if ((e.key === " " || e.code === "Space") && !selectedCard) {
        e.preventDefault();
        setRotation((r) => r + 1);
      }
    };
    window.addEventListener("keydown", handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, selectedCard]);

  // Resolve to cards that have usable imagery.
  const rendered = useMemo(
    () => allCards.filter((c) => c.card && (c.card.image_uris || c.card.card_faces)),
    [allCards],
  );

  // Featured 7 — the opening hand. Shuffle rotates which 7 are shown,
  // always one per role in priority order so the set reads like a
  // meaningful draw rather than a pile of basic lands.
  const featured = useMemo(
    () => pickFeatured(rendered, rotation),
    [rendered, rotation],
  );
  const featuredSlots = useMemo(
    () => featuredArc(featured.length),
    [featured.length],
  );

  const handleClassicDisplay = () => {
    setAutoOpen(false);
    onClose();
  };

  if (!open) return null;

  const total = entries.length;
  const resolved = rendered.length;

  // Portal-mount to <body> so the modal's `position: fixed` resolves
  // relative to the viewport regardless of ancestor CSS context (e.g.
  // the 3-pane app shell, transformed parents, contained scroll
  // containers). Without the portal, on some layouts the desktop
  // sidebar peeks through on the left edge — the modal anchors to a
  // containing-block-creating ancestor instead of the viewport.
  // z-index bumped above every chrome surface (mobile header z-40,
  // sidebar in normal flow, drawer z-50) so nothing can render over
  // the experience.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black anim-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Explore deck"
    >
      <StarfieldBackground />

      <Canvas
        className="absolute inset-0 z-10"
        camera={{ position: [0, 0, 18], fov: 60 }}
        gl={{ antialias: true, alpha: true }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.4} />
          <pointLight position={[10, 10, 10]} intensity={0.6} />
          <pointLight position={[-10, -10, -10]} intensity={0.3} />

          {/* Concentric wireframe spheres — ornament. */}
          <Sphere args={[2, 32, 32]} position={[0, 0, 0]}>
            <meshStandardMaterial color="#1a1a2e" transparent opacity={0.15} wireframe />
          </Sphere>
          <Sphere args={[14, 32, 32]} position={[0, 0, 0]}>
            <meshStandardMaterial color="#3b82f6" transparent opacity={0.04} wireframe />
          </Sphere>
          <Sphere args={[20, 32, 32]} position={[0, 0, 0]}>
            <meshStandardMaterial color="#3b82f6" transparent opacity={0.025} wireframe />
          </Sphere>
          <Sphere args={[28, 32, 32]} position={[0, 0, 0]}>
            <meshStandardMaterial color="#3b82f6" transparent opacity={0.015} wireframe />
          </Sphere>

          {/* Featured 7 — the opening hand, spaced so tiles don't overlap. */}
          {featured.map((card, i) => (
            <FloatingCard
              key={`feat-${rotation}-${card.copyId}`}
              card={card}
              position={featuredSlots[i]!}
              onSelect={setSelectedCard}
            />
          ))}

          <OrbitControls
            enablePan
            enableZoom
            enableRotate
            minDistance={6}
            maxDistance={50}
            rotateSpeed={0.5}
            zoomSpeed={1.2}
            panSpeed={0.8}
          />
        </Suspense>
      </Canvas>

      {/* Overlays */}
      <div className="absolute inset-0 pointer-events-none z-20">
        <div className="absolute top-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-2xl pointer-events-auto">
            {welcome && (
              <div className="eyebrow text-accent mb-2">Your deck, assembled</div>
            )}
            <h2 className="font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0] text-white">
              {welcome ? "Explore your deck." : title ?? "Explore deck"}
            </h2>
            {welcome && (
              <p className="mt-3 text-sm text-fg-muted max-w-md leading-relaxed">
                Drag to orbit. Scroll to zoom. Click a card to look closer.
              </p>
            )}
          </div>
        </div>

        <div className="absolute top-6 right-6 md:top-8 md:right-8 flex flex-col items-end gap-3 pointer-events-auto">
          <button
            type="button"
            onClick={onClose}
            className="text-fg-subtle hover:text-fg transition-colors"
            aria-label="Close explore deck"
          >
            <X size={20} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={handleClassicDisplay}
            className="btn-secondary text-xs h-9"
          >
            View classic display
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex flex-col items-center gap-4 pointer-events-none">
          <div className="font-mono text-[11px] text-fg-subtle tabular-nums">
            {resolved > 0 && (
              <>
                opening hand {featured.length} of {resolved}
                {resolved < total && (
                  <span className="text-fg-subtle/70"> · {total - resolved} unresolved</span>
                )}
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setRotation((r) => r + 1)}
            disabled={resolved <= FEATURED_COUNT}
            className={cn(
              "btn-primary h-11 px-6 pointer-events-auto",
              resolved <= FEATURED_COUNT && "opacity-40 cursor-not-allowed",
            )}
          >
            <Shuffle size={14} strokeWidth={2} />
            Shuffle hand
            <span className="text-[10px] font-mono opacity-70">space</span>
          </button>

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[11px] font-mono text-fg-subtle">
            <span className="inline-flex items-center gap-1.5">
              <Rotate3d size={12} strokeWidth={1.75} /> drag to orbit
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ZoomIn size={12} strokeWidth={1.75} />
              <ZoomOut size={12} strokeWidth={1.75} /> scroll to zoom
            </span>
            <span>click any card</span>
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-4">
              <div className="h-8 w-8 rounded-full border-2 border-border border-t-accent animate-spin" />
              <div className="eyebrow text-fg-subtle">Summoning cards…</div>
            </div>
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
            <div className="surface p-6 max-w-sm text-sm text-danger">
              {loadError}
            </div>
          </div>
        )}
      </div>

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </div>,
    document.body,
  );
}

/** Draw 7 cards at random from the deck, like the first hand of a game.
 *  Every Shuffle click re-draws — it's literally a Fisher-Yates pick of
 *  7 cards out of the 60. We pass `rotation` as the seed source so the
 *  selection is deterministic per click (re-renders during orbit don't
 *  re-roll) but changes every time the button is hit. */
function pickFeatured(cards: DeckCard[], rotation: number): DeckCard[] {
  if (cards.length === 0) return [];
  if (cards.length <= FEATURED_COUNT) return cards;

  // xmur3 + mulberry32 — small seeded PRNG so the 7 chosen don't flicker
  // on unrelated re-renders. `rotation` feeds the seed so each Shuffle
  // click yields a new shuffle of the deck.
  const seedHash = (() => {
    let h = 1779033703 ^ rotation;
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  })();
  let state = seedHash || 1;
  const rand = () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Fisher-Yates pick of FEATURED_COUNT distinct cards.
  const indices = cards.map((_, i) => i);
  for (let i = 0; i < FEATURED_COUNT; i++) {
    const j = i + Math.floor(rand() * (indices.length - i));
    [indices[i], indices[j]] = [indices[j]!, indices[i]!];
  }
  return indices.slice(0, FEATURED_COUNT).map((i) => cards[i]!);
}

/** 7 cards in a gentle horizontal arc facing the camera. Spacing is
 *  tuned so tiles don't overlap: at the chosen plane size (4.2 × 5.9)
 *  and arc radius, 7 slots across a 90° sweep leaves ~0.5 unit of
 *  breathing room between neighboring cards. Slight vertical wave so
 *  the hand reads as a curve, not a ruler. */
function featuredArc(count: number): Slot[] {
  const positions: Slot[] = [];
  if (count === 0) return positions;
  // Wider radius + wider sweep = more horizontal spread between cards.
  // Previous 9 × 70° touched at the ends with 7 cards; 11 × 90° gives
  // comfortable gaps.
  const radius = 11;
  const sweep = Math.PI * 0.9;
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0 : i / (count - 1);
    const angle = (t - 0.5) * sweep;
    const x = Math.sin(angle) * radius;
    const z = 2 - Math.cos(angle) * radius * 0.3;
    const y = Math.sin(t * Math.PI * 1.4 + 0.3) * 0.9;
    positions.push({ x, y, z });
  }
  return positions;
}

/** A single card tile in the opening hand. Faces the camera, scales on
 *  hover, opens the detail modal on click. */
function FloatingCard({
  card,
  position,
  onSelect,
}: {
  card: DeckCard;
  position: Slot;
  onSelect: (card: DeckCard) => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);

  useFrame(({ camera }) => {
    if (groupRef.current) groupRef.current.lookAt(camera.position);
  });

  const imageUrl = card.card && imageFor(card.card, "normal");
  if (!imageUrl) return null;

  const planeSize: [number, number] = [4.2, 5.9];
  const distanceFactor = 9;
  const tileWidth = 190;
  const tileHeight = 266;

  return (
    <group ref={groupRef} position={[position.x, position.y, position.z]}>
      <Plane
        args={planeSize}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(card);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setHovered(false);
          document.body.style.cursor = "auto";
        }}
      >
        <meshBasicMaterial transparent opacity={0} />
      </Plane>

      <Html
        transform
        distanceFactor={distanceFactor}
        position={[0, 0, 0.01]}
        style={{
          transition: "transform 0.25s ease",
          transform: hovered ? "scale(1.12)" : "scale(1)",
          pointerEvents: "none",
        }}
      >
        <div
          className="rounded-xl overflow-hidden select-none"
          style={{
            width: tileWidth,
            height: tileHeight,
            boxShadow: hovered
              ? "0 0 40px rgba(59, 130, 246, 0.55), 0 0 80px rgba(59, 130, 246, 0.25)"
              : "0 10px 30px rgba(0, 0, 0, 0.6)",
            border: hovered
              ? "1.5px solid rgba(59, 130, 246, 0.8)"
              : "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <img
            src={imageUrl}
            alt={card.name}
            loading="lazy"
            draggable={false}
            className="block w-full h-full object-cover"
          />
        </div>
        <div
          className="mt-1.5 text-center font-mono text-[10px] text-fg-muted tabular-nums whitespace-nowrap"
          style={{ width: tileWidth }}
        >
          <span className="text-accent">{card.count}x</span>{" "}
          <span className="text-fg">{truncate(card.name, 22)}</span>
        </div>
      </Html>
    </group>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function CardDetailModal({
  card,
  onClose,
}: {
  card: DeckCard;
  onClose: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const imageUrl =
    (card.card && imageFor(card.card, "large")) ||
    (card.card && imageFor(card.card, "normal"));
  const backUrl = card.card ? backImageFor(card.card, "large") : null;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = (y - cy) / 22;
    const rotateY = (cx - x) / 22;
    el.style.transition = "transform 0.05s linear";
    el.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transition = "transform 0.5s ease-out";
    el.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg)";
  };

  const backdropStyle: CSSProperties = {
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 anim-fade-in"
      style={backdropStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-w-md w-full mx-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-fg-muted transition-colors z-10"
          aria-label="Close card detail"
        >
          <X size={22} strokeWidth={1.75} />
        </button>

        <div style={{ perspective: "1000px" }}>
          <div
            ref={cardRef}
            className="relative rounded-2xl bg-bg-surface p-4 transition-transform duration-500 ease-out"
            style={{
              transformStyle: "preserve-3d",
              boxShadow:
                "0 50px 100px rgba(0, 0, 0, 0.6), 0 20px 40px rgba(0, 0, 0, 0.4)",
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {imageUrl && (
              <div className="w-full mb-4">
                <CardImage
                  front={imageUrl}
                  back={backUrl}
                  alt={card.name}
                  draggable={false}
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-display text-xl text-white tracking-tight truncate">
                  {card.name}
                </h3>
                {card.card?.mana_cost && (
                  <ManaCost cost={card.card.mana_cost} size="md" />
                )}
              </div>
              {card.card?.type_line && (
                <div className="font-mono text-[11px] uppercase tracking-widest text-fg-subtle">
                  {card.card.type_line}
                </div>
              )}
              <div className="flex items-center gap-3 pt-1 text-xs text-fg-muted">
                <span>
                  <span className="text-accent font-semibold">{card.count}</span>
                  <span className="text-fg-subtle"> in deck</span>
                </span>
                {card.card?.set_name && (
                  <span className="text-fg-subtle truncate">
                    · {card.card.set_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StarfieldBackground() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      2000,
    );
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const geo = new THREE.BufferGeometry();
    const count = 10000;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2000;
    }
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.7,
      sizeAttenuation: true,
    });
    const stars = new THREE.Points(geo, mat);
    scene.add(stars);
    camera.position.z = 10;

    let animationId = 0;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      stars.rotation.y += 0.0001;
      stars.rotation.x += 0.00005;
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      geo.dispose();
      mat.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      aria-hidden
      className="absolute inset-0 pointer-events-none"
    />
  );
}
